
import 'reflect-metadata';
import { injectable, inject } from 'inversify-props';
import { ISettings } from './SettingsService';
import { ILogger } from './LoggerService';
import { IStorage } from './StorageService';

declare const window: any;

/** Handles sending usage analytics to the analytics service */
export interface IAnalytics {

	/**
	 * Sends an analytics event to the analytics service
	 * @param category The category this event falls in.
	 * @param action The action that was performed
	 * @param label A label to classify it better
	 * @param value A value of the event if available.
	 */
	event(event: string, category: string, action: string, label: string, value?: number): void;

	/**
	 * Emits a page view to the analytics service.
	 * @param page The page being viewed.
	 */
	pageView(page: string): void;

	/**
	 * Emits a timing to the analytics service to track how long things are taking.
	 * @param category The category of this timing
	 * @param timingVar The name of the timing
	 * @param value The amount of time in milliseconds it took for this timing.
	 */
	timing(category: string, timingVar: string, value: number): void;

	/**
	 * Starts a timer with the specified name.
	 * @param name The name of the timer to start.
	 */
	startTimer(name: string): void;

	/**
	 * Stops the specified timer and logs it as a timing event in the analytics service.
	 * @param name The name of the timer to stop.
	 * @param category A category to further classify this timing.
	 */
	stopTimer(name: string, category: string): void;

}

/** Handles sending usage analytics to the analytics service */
@injectable()
export class AnalyticsService implements IAnalytics {


	constructor(
		@inject('ISettings') private settings: ISettings,
		@inject('IStorage') private storage: IStorage,
		@inject('ILogger') private logger: ILogger) { }


	/**
	 * Sends an analytics event to the analytics service
	 * @param category The category this event falls in.
	 * @param action The action that was performed
	 * @param label A label to classify it better
	 * @param value A value of the event if available.
	 */
	public event(event: string, category: string, action: string, label: string, value?: number): void {
		const payload = {
			event,
			category,
			action,
			label,
			value,
			noninteraction: false,
		};
		window.$gtm.trackEvent(payload);
	}

	/**
	 * Emits a page view to the analytics service.
	 * @param page The page being viewed.
	 */
	public pageView(page: string): void {
		// TODO: Fill this out
	}

	/**
	 * Emits a timing to the analytics service to track how long things are taking.
	 * @param category The category of this timing
	 * @param timingVar The name of the timing
	 * @param value The amount of time in milliseconds it took for this timing.
	 */
	public timing(category: string, timingVar: string, value: number): void {
		// TODO: Fill this out
	}

	/**
	 * Starts a timer with the specified name.
	 * @param name The name of the timer to start.
	 */
	public startTimer(name: string): void {
		const key = btoa(name);
		const start = new Date().toString();
		this.storage.store(key, start);
		this.logger.log(`[ANALYTICS] Starting timer at: ${start}`);

	}

	/**
	 * Stops the specified timer and logs it as a timing event in the analytics service.
	 * @param name The name of the timer to stop.
	 * @param category A category to further classify this timing.
	 */
	public stopTimer(name: string, category: string): void {
		const key = btoa(name);
		const start = new Date(this.storage.retrieve(key));
		const end = new Date();
		const elapsed = (end.getTime() - start.getTime());
		this.logger.log(`[ANALYTICS] Stopping timer at: ${end.toString()}.  Elapsed time: ${elapsed}ms`);
		this.timing(category, name, elapsed);
	}

}
